'use client';

import { useState, useEffect, useCallback } from 'react';

import { notificationApi } from 'src/lib/notification-api';

export function useNotifications(options = {}) {
    const { limit = 20, autoRefresh = false, refreshInterval = 30000 } = options;

    const [notifications, setNotifications] = useState([]);
    const [stats, setStats] = useState({ total: 0, unread: 0, archived: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    // Fetch notifications
    const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
        try {
            setLoading(true);
            setError(null);

            const [notificationsData, statsData] = await Promise.all([
                notificationApi.getNotifications({ page: pageNum, limit }),
                notificationApi.getStats(),
            ]);

            const formattedNotifications = notificationsData.data.map((notification) => ({
                id: notification.id,
                title: notification.title,
                message: notification.message,
                type: mapNotificationType(notification.type),
                category: getCategoryFromType(notification.type),
                isUnRead: !notification.is_read,
                createdAt: notification.created_at,
                avatarUrl: null, // Can be extended to include user avatars
                originalType: notification.type,
                metadata: notification.metadata || null,
                facilityId: notification.facility_id || null,
                recipientRole: notification.recipient_role || 'USER',
            }));

            if (append) {
                setNotifications((prev) => [...prev, ...formattedNotifications]);
            } else {
                setNotifications(formattedNotifications);
            }

            setStats({
                total: statsData.total,
                unread: statsData.unread,
                archived: statsData.archived,
            });

            setHasMore(pageNum < notificationsData.total_pages);
            setPage(pageNum);
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError(err.message || 'Failed to fetch notifications');
        } finally {
            setLoading(false);
        }
    }, [limit]);

    // Mark single notification as read
    const markAsRead = useCallback(async (id) => {
        try {
            await notificationApi.markAsRead(id);
            setNotifications((prev) =>
                prev.map((notification) =>
                    notification.id === id ? { ...notification, isUnRead: false } : notification
                )
            );
            setStats((prev) => ({
                ...prev,
                unread: Math.max(0, prev.unread - 1),
                archived: prev.archived + 1,
            }));
        } catch (err) {
            console.error('Error marking notification as read:', err);
            throw err;
        }
    }, []);

    // Mark all notifications as read
    const markAllAsRead = useCallback(async () => {
        try {
            await notificationApi.markAllAsRead();
            setNotifications((prev) =>
                prev.map((notification) => ({ ...notification, isUnRead: false }))
            );
            setStats((prev) => ({
                ...prev,
                unread: 0,
                archived: prev.total,
            }));
        } catch (err) {
            console.error('Error marking all notifications as read:', err);
            throw err;
        }
    }, []);

    // Delete notification
    const deleteNotification = useCallback(async (id) => {
        try {
            await notificationApi.deleteNotification(id);
            setNotifications((prev) => prev.filter((notification) => notification.id !== id));
            setStats((prev) => ({
                ...prev,
                total: prev.total - 1,
                unread: prev.unread - (notifications.find((n) => n.id === id)?.isUnRead ? 1 : 0),
            }));
        } catch (err) {
            console.error('Error deleting notification:', err);
            throw err;
        }
    }, [notifications]);

    // Load more notifications
    const loadMore = useCallback(() => {
        if (!loading && hasMore) {
            fetchNotifications(page + 1, true);
        }
    }, [loading, hasMore, page, fetchNotifications]);

    // Refresh notifications
    const refresh = useCallback(() => {
        fetchNotifications(1, false);
    }, [fetchNotifications]);

    // Initial fetch
    useEffect(() => {
        fetchNotifications(1, false);
    }, [fetchNotifications]);

    // Auto refresh
    useEffect(() => {
        if (autoRefresh && refreshInterval > 0) {
            const intervalId = setInterval(() => {
                fetchNotifications(1, false);
            }, refreshInterval);

            return () => clearInterval(intervalId);
        }
        return undefined;
    }, [autoRefresh, refreshInterval, fetchNotifications]);

    return {
        notifications,
        stats,
        loading,
        error,
        hasMore,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        loadMore,
        refresh,
    };
}

// Map backend notification types to frontend display types
function mapNotificationType(backendType) {
    const typeMap = {
        SESSION_INVITATION: 'session',
        SESSION_REMINDER: 'session',
        SESSION_CANCELLED: 'session',
        PAYMENT_REQUIRED: 'payment',
        PAYMENT_RECEIVED: 'payment',
        BOOKING_APPROVED: 'booking',
        BOOKING_CANCELLED: 'booking',
        MATCH_RESULT: 'match',
        PROFILE_UPDATE: 'profile',
        GENERAL: 'general',
        BOOKING_CREATED_FACILITY: 'booking',
        BOOKING_CANCELLED_FACILITY: 'booking',
        BOOKING_REBOOKED_FACILITY: 'booking',
        SESSION_JOIN_REQUESTED_FACILITY: 'session',
        SESSION_LEFT_FACILITY: 'session',
        SESSION_CANCELLED_FACILITY: 'session',
        PAYMENT_RECEIPT_SUBMITTED_FACILITY: 'payment',
        RECURRING_SESSION_ISSUE_FACILITY: 'session',
    };

    return typeMap[backendType] || 'general';
}

// Get category label from notification type
function getCategoryFromType(type) {
    const categoryMap = {
        SESSION_INVITATION: 'Sessions',
        SESSION_REMINDER: 'Sessions',
        SESSION_CANCELLED: 'Sessions',
        PAYMENT_REQUIRED: 'Payments',
        PAYMENT_RECEIVED: 'Payments',
        BOOKING_APPROVED: 'Bookings',
        BOOKING_CANCELLED: 'Bookings',
        MATCH_RESULT: 'Matches',
        PROFILE_UPDATE: 'Profile',
        GENERAL: 'General',
        BOOKING_CREATED_FACILITY: 'Bookings',
        BOOKING_CANCELLED_FACILITY: 'Bookings',
        BOOKING_REBOOKED_FACILITY: 'Bookings',
        SESSION_JOIN_REQUESTED_FACILITY: 'Sessions',
        SESSION_LEFT_FACILITY: 'Sessions',
        SESSION_CANCELLED_FACILITY: 'Sessions',
        PAYMENT_RECEIPT_SUBMITTED_FACILITY: 'Payments',
        RECURRING_SESSION_ISSUE_FACILITY: 'Sessions',
    };

    return categoryMap[type] || 'General';
}

export default useNotifications;
